import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Chip,
  Tooltip,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import ChatIcon from '@material-ui/icons/Chat';
import EditIcon from '@material-ui/icons/Edit';
import { format, parseISO, isSameDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';

const useStyles = makeStyles(theme => ({
  card: {
    background: '#ffffff',
    borderRadius: 8,
    border: '1px solid #ebebeb',
    marginBottom: 6,
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 12px',
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      borderColor: '#d8d8d8',
    },
  },
  cardDragging: {
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    borderColor: '#c8c8c8',
  },
  topRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8,
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: '0.75rem',
    flexShrink: 0,
    marginTop: 1,
  },
  nameBlock: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1a1a1a',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.3,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    marginTop: 1,
  },
  ticketNum: {
    fontSize: '0.65rem',
    color: '#b0b0b0',
    lineHeight: 1.2,
    flexShrink: 0,
  },
  connectionDot: {
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: '#d0d0d0',
    flexShrink: 0,
  },
  connectionName: {
    fontSize: '0.65rem',
    color: '#b0b0b0',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  agentInMeta: {
    fontSize: '0.6rem',
    fontWeight: 700,
    color: '#7b7b7b',
    background: '#f0f0f0',
    borderRadius: 3,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    lineHeight: 1.4,
  },
  lastMessage: {
    fontSize: '0.75rem',
    color: '#9b9b9b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: 8,
    lineHeight: 1.4,
  },
  valuePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#2e7d32',
    background: '#f1f8f1',
    borderRadius: 5,
    padding: '2px 7px',
    cursor: 'pointer',
    marginBottom: 8,
    '&:hover': { background: '#e8f5e9' },
  },
  noValuePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.72rem',
    color: '#c0c0c0',
    background: '#f9f9f9',
    borderRadius: 5,
    padding: '2px 7px',
    cursor: 'pointer',
    marginBottom: 8,
    '&:hover': { background: '#f0f0f0', color: '#888' },
  },
  divider: {
    background: '#f0f0f0',
    marginBottom: 8,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  agentTag: {
    fontSize: '0.62rem',
    fontWeight: 600,
    color: '#6b6b6b',
    background: '#f0f0f0',
    borderRadius: 4,
    padding: '2px 6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 72,
    flexShrink: 0,
  },
  footerTagChip: {
    fontSize: '0.58rem',
    fontWeight: 700,
    borderRadius: 4,
    padding: '2px 5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 52,
    flexShrink: 0,
    lineHeight: 1.4,
  },
  footerTagMore: {
    fontSize: '0.58rem',
    fontWeight: 600,
    color: '#757575',
    background: '#f0f0f0',
    borderRadius: 4,
    padding: '2px 5px',
    flexShrink: 0,
    lineHeight: 1.4,
  },
  spacer: { flex: 1 },
  timeText: {
    fontSize: '0.65rem',
    color: '#b0b0b0',
    flexShrink: 0,
  },
  timeTextUnread: {
    fontSize: '0.65rem',
    color: '#43a047',
    fontWeight: 700,
    flexShrink: 0,
  },
  iconBtn: {
    padding: 4,
    color: '#b0b0b0',
    '&:hover': { color: '#1a1a1a', background: '#f5f5f5' },
  },
  iconBtnPrimary: {
    padding: 4,
    color: '#1a1a1a',
    '&:hover': { color: '#1a1a1a', background: '#f0f0f0' },
  },
  // Modals
  dialogPaper: {
    borderRadius: 12,
    minWidth: 380,
  },
  dialogTitleRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  sectionLabel: {
    fontSize: '0.7rem',
    color: '#9b9b9b',
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.05em',
    marginTop: 8,
    marginBottom: 4,
  },
  inputField: {
    '& .MuiOutlinedInput-root': { borderRadius: 8, fontSize: '0.875rem' },
    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
  },
  readonlyField: {
    fontSize: '0.875rem',
    color: '#6b6b6b',
    padding: '9px 12px',
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    background: '#fafafa',
    lineHeight: 1.5,
  },
  readonlyLabel: {
    fontSize: '0.75rem',
    color: '#9b9b9b',
    marginBottom: 4,
  },
  saveBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  cancelBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontSize: '0.85rem',
    color: '#6b6b6b',
  },
}));

const getExtraField = (extraInfo, fieldName) =>
  (extraInfo || []).find(f => f.name === fieldName)?.value || '';

const KanbanCard = ({ ticket, index, updateTicket, removeTicket, connections = [] }) => {
  const classes = useStyles();
  const history = useHistory();

  const [valueOpen, setValueOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [localConnections, setLocalConnections] = useState(connections);

  // Tags normais (kanban=0) no ticket
  const [ticketTags, setTicketTags] = useState([]);
  // Snapshot das tags de etapa (kanban=1) para preservar ao atualizar
  const [kanbanTagsSnapshot, setKanbanTagsSnapshot] = useState([]);
  // Tags normais disponíveis na empresa
  const [availableTags, setAvailableTags] = useState([]);
  // Tag selecionada no dropdown para adicionar
  const [tagToAdd, setTagToAdd] = useState('');

  const customFields = ticket.contact.extraInfo || [];
  const valueField = customFields.find(f => f.name === 'valor');
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;

  const connectionName = ticket.whatsapp?.name || null;
  const timeClass = Number(ticket.unreadMessages) > 0 ? classes.timeTextUnread : classes.timeText;

  // Tags normais no card (kanban=0), máx 2 visíveis
  const normalTags = (ticket.tags || []).filter(t => t.kanban !== 1);
  const visibleTags = normalTags.slice(0, 2);
  const hiddenTagsCount = normalTags.length - visibleTags.length;

  const handleCardClick = () => history.push(`/tickets/${ticket.uuid}`);

  // --- Valor modal ---
  const handleOpenValue = (e) => {
    e.stopPropagation();
    setNewValue(valueField ? valueField.value.toString() : '');
    setValueOpen(true);
  };
  const handleCloseValue = () => setValueOpen(false);
  const handleSaveValue = async () => {
    try {
      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: [...customFields.filter(f => f.name !== 'valor'), { name: 'valor', value: newValue }],
      });
      if (valueField) valueField.value = newValue;
      else ticket.contact.extraInfo = [...customFields, { name: 'valor', value: newValue }];
      updateTicket({ ...ticket });
      setValueOpen(false);
    } catch (error) {
      console.error('Erro ao salvar valor:', error);
    }
  };
  const handleRemoveValue = async (e) => {
    e.stopPropagation();
    try {
      const newFields = customFields.filter(f => f.name !== 'valor');
      await api.put(`/contacts/${ticket.contact.id}`, { extraInfo: newFields });
      ticket.contact.extraInfo = newFields;
      updateTicket({ ...ticket });
    } catch (error) {
      console.error('Erro ao remover valor:', error);
    }
  };

  // --- Lead modal ---
  const handleOpenLead = async (e) => {
    e.stopPropagation();
    setLeadForm({
      name: ticket.contact.name || '',
      number: ticket.contact.number || '',
      email: ticket.contact.email || '',
      empresa: getExtraField(customFields, 'empresa'),
      servico: getExtraField(customFields, 'servico'),
      valor: getExtraField(customFields, 'valor'),
      whatsappId: ticket.whatsappId || '',
    });
    if (connections.length > 0) {
      setLocalConnections(connections);
    } else {
      try {
        const { data } = await api.get('/whatsapp');
        setLocalConnections(data || []);
      } catch {
        setLocalConnections([]);
      }
    }

    // Separar tags do ticket: kanban=1 (etapas) vs kanban=0 (normais)
    const allTicketTags = ticket.tags || [];
    setKanbanTagsSnapshot(allTicketTags.filter(t => t.kanban === 1));
    setTicketTags(allTicketTags.filter(t => t.kanban !== 1));
    setTagToAdd('');

    // Carrega tags normais disponíveis na empresa
    try {
      const { data } = await api.get('/tags/list', { params: { kanban: 0 } });
      setAvailableTags(data || []);
    } catch {
      setAvailableTags([]);
    }

    setLeadOpen(true);
  };
  const handleCloseLead = () => setLeadOpen(false);

  // Adiciona uma tag normal ao ticket
  const handleAddTag = async () => {
    if (!tagToAdd) return;
    const tag = availableTags.find(t => String(t.id) === String(tagToAdd));
    if (!tag || ticketTags.some(t => t.id === tag.id)) return;
    try {
      await api.put(`/ticket-tags/${ticket.id}/${tag.id}`);
      const updated = [...ticketTags, tag];
      setTicketTags(updated);
      ticket.tags = [...kanbanTagsSnapshot, ...updated];
      updateTicket({ ...ticket });
      setTagToAdd('');
    } catch (error) {
      console.error('Erro ao adicionar tag:', error);
    }
  };

  // Remove uma tag normal do ticket
  const handleRemoveTag = async (tagId) => {
    try {
      await api.delete(`/ticket-tags/${ticket.id}/${tagId}`);
      const updated = ticketTags.filter(t => t.id !== tagId);
      setTicketTags(updated);
      ticket.tags = [...kanbanTagsSnapshot, ...updated];
      updateTicket({ ...ticket });
    } catch (error) {
      console.error('Erro ao remover tag:', error);
    }
  };

  const handleLeadChange = field => e => {
    const value = e && e.target ? e.target.value : '';
    setLeadForm(prev => ({ ...prev, [field]: value }));
  };
  const handleDeleteLead = async () => {
    if (!window.confirm(`Deseja excluir o lead "${ticket.contact.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/tickets/${ticket.id}`);
      await api.delete(`/contacts/${ticket.contact.id}`);
      removeTicket(ticket.id);
      setLeadOpen(false);
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
    }
  };
  const handleSaveLead = async () => {
    setSaving(true);
    try {
      const extraInfoMap = {};
      (customFields || []).forEach(f => { extraInfoMap[f.name] = f.value; });
      extraInfoMap['empresa'] = leadForm.empresa;
      extraInfoMap['servico'] = leadForm.servico;
      extraInfoMap['valor'] = leadForm.valor;
      const extraInfo = Object.entries(extraInfoMap)
        .filter(([, v]) => v !== '' && v !== undefined && v !== null)
        .map(([name, value]) => ({ name, value: String(value) }));
      await api.put(`/contacts/${ticket.contact.id}`, {
        name: leadForm.name,
        number: leadForm.number,
        email: leadForm.email,
        extraInfo,
      });
      if (leadForm.whatsappId !== undefined && leadForm.whatsappId !== (ticket.whatsappId || '')) {
        await api.put(`/tickets/${ticket.id}`, { whatsappId: leadForm.whatsappId || null });
        ticket.whatsappId = leadForm.whatsappId || null;
      }
      ticket.contact.name = leadForm.name;
      ticket.contact.number = leadForm.number;
      ticket.contact.email = leadForm.email;
      ticket.contact.extraInfo = extraInfo;
      updateTicket({ ...ticket });
      setLeadOpen(false);
    } catch (error) {
      console.error('Erro ao salvar lead:', error);
    } finally {
      setSaving(false);
    }
  };

  const formattedTime = isSameDay(parseISO(ticket.updatedAt), new Date())
    ? format(parseISO(ticket.updatedAt), 'HH:mm')
    : format(parseISO(ticket.updatedAt), 'dd/MM');

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={`${classes.card} ${snapshot.isDragging ? classes.cardDragging : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          {/* Topo: avatar + nome + #id + conexão */}
          <div className={classes.topRow}>
            <Avatar src={ticket.contact.urlPicture} className={classes.avatar}>
              {ticket.contact.name?.[0]?.toUpperCase()}
            </Avatar>
            <div className={classes.nameBlock}>
              <Tooltip title={ticket.contact.name || ''}>
                <Typography className={classes.contactName}>
                  {ticket.contact.name || '—'}
                </Typography>
              </Tooltip>
              <div className={classes.metaRow}>
                <span className={classes.ticketNum}>#{ticket.id}</span>
                {connectionName && (
                  <>
                    <span className={classes.connectionDot} />
                    <Tooltip title={`Conexão: ${connectionName}`}>
                      <span className={classes.connectionName}>{connectionName}</span>
                    </Tooltip>
                  </>
                )}
                {ticket.user && (
                  <>
                    <span className={classes.connectionDot} />
                    <Tooltip title={ticket.user.name}>
                      <span className={classes.agentInMeta}>
                        {ticket.user.name.split(' ')[0].toUpperCase()}
                      </span>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Última mensagem */}
          {ticket.lastMessage ? (
            <Tooltip title={ticket.lastMessage}>
              <Typography className={classes.lastMessage}>{ticket.lastMessage.substring(0, 45)}</Typography>
            </Tooltip>
          ) : (
            <Typography className={classes.lastMessage} style={{ fontStyle: 'italic' }}>Sem mensagens</Typography>
          )}

          {/* Valor */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
            <span
              className={opportunityValue !== null ? classes.valuePill : classes.noValuePill}
              onClick={handleOpenValue}
            >
              <AttachMoneyIcon style={{ fontSize: 11 }} />
              {opportunityValue !== null
                ? `R$ ${opportunityValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                : 'Valor'}
            </span>
            {opportunityValue !== null && (
              <Tooltip title="Remover valor">
                <IconButton size="small" onClick={handleRemoveValue} style={{ padding: 2, color: '#ccc' }}>
                  <CloseIcon style={{ fontSize: 11 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <Divider className={classes.divider} />

          {/* Footer: tags normais | espaço | ícones */}
          <div className={classes.footer}>
            {visibleTags.map(tag => (
              <Tooltip key={tag.id} title={tag.name}>
                <span
                  className={classes.footerTagChip}
                  style={{
                    background: tag.color ? `${tag.color}22` : '#f0f0f0',
                    color: tag.color || '#555',
                    border: `1px solid ${tag.color ? `${tag.color}55` : '#e0e0e0'}`,
                  }}
                >
                  {tag.name}
                </span>
              </Tooltip>
            ))}
            {hiddenTagsCount > 0 && (
              <Tooltip title={normalTags.slice(2).map(t => t.name).join(', ')}>
                <span className={classes.footerTagMore}>+{hiddenTagsCount}</span>
              </Tooltip>
            )}
            <span className={classes.spacer} />
            <Tooltip title="Abrir conversa">
              <IconButton size="small" className={classes.iconBtn} onClick={handleCardClick}>
                <ChatIcon style={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dados do Lead">
              <IconButton size="small" className={classes.iconBtnPrimary} onClick={handleOpenLead}>
                <EditIcon style={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </div>

          {/* Modal: Editar Valor */}
          <Dialog open={valueOpen} onClose={handleCloseValue} classes={{ paper: classes.dialogPaper }}>
            <DialogTitle disableTypography className={classes.dialogTitleRoot}>
              <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 700 }}>
                {valueField ? 'Editar' : 'Definir'} Valor
              </Typography>
              <IconButton size="small" onClick={handleCloseValue}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ paddingTop: 16 }}>
              <TextField
                label="Valor (R$)"
                type="number"
                fullWidth
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.inputField}
                autoFocus
              />
            </DialogContent>
            <DialogActions style={{ padding: '12px 24px' }}>
              <Button onClick={handleCloseValue} className={classes.cancelBtn}>Cancelar</Button>
              <Button onClick={handleSaveValue} color="primary" variant="contained" className={classes.saveBtn}>Salvar</Button>
            </DialogActions>
          </Dialog>

          {/* Modal: Dados do Lead */}
          <Dialog
            open={leadOpen}
            onClose={handleCloseLead}
            classes={{ paper: classes.dialogPaper }}
            onClick={e => e.stopPropagation()}
          >
            <DialogTitle disableTypography className={classes.dialogTitleRoot}>
              <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 700 }}>Dados do Lead</Typography>
              <IconButton size="small" onClick={handleCloseLead}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Grid container spacing={2} style={{ marginTop: 2 }}>
                <Grid item xs={12}>
                  <Typography className={classes.sectionLabel}>Contato</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Nome" fullWidth value={leadForm.name || ''} onChange={handleLeadChange('name')} variant="outlined" size="small" className={classes.inputField} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Telefone" fullWidth value={leadForm.number || ''} onChange={handleLeadChange('number')} variant="outlined" size="small" className={classes.inputField} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="E-mail" fullWidth value={leadForm.email || ''} onChange={handleLeadChange('email')} variant="outlined" size="small" className={classes.inputField} />
                </Grid>

                <Grid item xs={12}>
                  <Typography className={classes.sectionLabel}>Conexão</Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Conexão WhatsApp</InputLabel>
                    <Select
                      value={leadForm.whatsappId || ''}
                      onChange={e => setLeadForm(prev => ({ ...prev, whatsappId: e.target.value }))}
                      label="Conexão WhatsApp"
                    >
                      <MenuItem value=""><em>Sem conexão</em></MenuItem>
                      {localConnections.map(c => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}{c.status !== 'CONNECTED' ? ' (desconectado)' : ''}{c.isDefault ? ' ★' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography className={classes.sectionLabel}>Negócio</Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Empresa" fullWidth value={leadForm.empresa || ''} onChange={handleLeadChange('empresa')} variant="outlined" size="small" className={classes.inputField} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Serviço / Produto" fullWidth value={leadForm.servico || ''} onChange={handleLeadChange('servico')} variant="outlined" size="small" className={classes.inputField} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Valor Negociado (R$)" type="number" fullWidth value={leadForm.valor || ''} onChange={handleLeadChange('valor')} variant="outlined" size="small" className={classes.inputField} />
                </Grid>
                <Grid item xs={6}>
                  <Typography className={classes.readonlyLabel}>Criado em</Typography>
                  <Typography className={classes.readonlyField}>
                    {ticket.createdAt ? format(parseISO(ticket.createdAt), 'dd/MM/yyyy HH:mm') : '—'}
                  </Typography>
                </Grid>

                {/* Tags normais */}
                <Grid item xs={12}>
                  <Typography className={classes.sectionLabel}>Tags</Typography>
                </Grid>
                <Grid item xs={12}>
                  {/* Chips das tags atuais */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 28, marginBottom: 10 }}>
                    {ticketTags.length === 0 ? (
                      <Typography style={{ fontSize: '0.78rem', color: '#b0b0b0', fontStyle: 'italic', lineHeight: '28px' }}>
                        Nenhuma tag
                      </Typography>
                    ) : (
                      ticketTags.map(tag => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          onDelete={() => handleRemoveTag(tag.id)}
                          style={{
                            background: tag.color ? `${tag.color}22` : '#f0f0f0',
                            color: tag.color || '#555',
                            border: `1px solid ${tag.color || '#e0e0e0'}`,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            height: 24,
                          }}
                        />
                      ))
                    )}
                  </div>
                  {/* Adicionar nova tag */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                      <InputLabel style={{ fontSize: '0.875rem' }}>Adicionar tag</InputLabel>
                      <Select
                        value={tagToAdd}
                        onChange={e => setTagToAdd(e.target.value)}
                        label="Adicionar tag"
                        style={{ borderRadius: 8, fontSize: '0.875rem' }}
                      >
                        <MenuItem value=""><em>Selecione uma tag</em></MenuItem>
                        {availableTags
                          .filter(t => !ticketTags.some(tt => tt.id === t.id))
                          .map(t => (
                            <MenuItem key={t.id} value={t.id}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {t.color && (
                                  <span style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: t.color, display: 'inline-block', flexShrink: 0
                                  }} />
                                )}
                                {t.name}
                              </span>
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddTag}
                      disabled={!tagToAdd}
                      style={{
                        height: 38, borderRadius: 8, textTransform: 'none',
                        whiteSpace: 'nowrap', fontSize: '0.8rem', flexShrink: 0
                      }}
                    >
                      Adicionar
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions style={{ padding: '12px 24px' }}>
              <Button
                onClick={handleDeleteLead}
                variant="outlined"
                startIcon={<DeleteIcon />}
                style={{ marginRight: 'auto', color: '#d32f2f', borderColor: '#d32f2f', borderRadius: 8, textTransform: 'none', fontSize: '0.85rem' }}
              >
                Excluir Lead
              </Button>
              <Button onClick={handleCloseLead} className={classes.cancelBtn}>Cancelar</Button>
              <Button onClick={handleSaveLead} color="primary" variant="contained" className={classes.saveBtn} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
